import React from 'react'
import PropTypes from 'prop-types'
import styles from './Button.module.scss'

export const BUTTON_VARIANTS = [
  'primary',
  'accent',
  'orange',
  'secondary',
  'ghost',
  'danger',
]

export const BUTTON_SIZES = ['small', 'medium', 'large']

const cx = (...parts) => parts.filter(Boolean).join(' ')

const Button = React.forwardRef(function Button(props, ref) {
  const {
    variant = 'primary',
    size = 'medium',
    pill = false,
    uppercase = false,
    block = false,
    loading = false,
    disabled = false,
    startIcon,
    endIcon,
    href,
    as,
    type = 'button',
    className,
    children,
    ...rest
  } = props

  const classes = cx(
    styles.btn,
    styles[variant],
    styles[size],
    pill && styles.pill,
    uppercase && styles.uppercase,
    block && styles.block,
    className
  )

  const content = (
    <>
      {loading ? <span className={styles.spinner} aria-hidden="true" /> : startIcon}
      {children}
      {!loading && endIcon}
    </>
  )

  if (href && !disabled && !loading) {
    return (
      <a ref={ref} className={classes} href={href} {...rest}>
        {content}
      </a>
    )
  }

  if (as && as !== 'button') {
    const Tag = as
    return (
      <Tag
        ref={ref}
        className={classes}
        aria-disabled={disabled || loading || undefined}
        aria-busy={loading || undefined}
        {...rest}
      >
        {content}
      </Tag>
    )
  }

  return (
    <button
      ref={ref}
      type={type}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {content}
    </button>
  )
})

Button.propTypes = {
  variant: PropTypes.oneOf(BUTTON_VARIANTS),
  size: PropTypes.oneOf(BUTTON_SIZES),

  pill: PropTypes.bool,

  uppercase: PropTypes.bool,

  block: PropTypes.bool,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  startIcon: PropTypes.node,
  endIcon: PropTypes.node,
  href: PropTypes.string,

  as: PropTypes.elementType,
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  className: PropTypes.string,
  children: PropTypes.node,
}

export const IconButton = React.forwardRef(function IconButton(props, ref) {
  const { label, variant = 'ghost', size = 'medium', className, children, ...rest } = props

  if (process.env.NODE_ENV !== 'production' && !label) {
    throw new Error('IconButton requires a `label` — icon-only controls must have an accessible name.')
  }

  return (
    <Button
      ref={ref}
      variant={variant}
      size={size}
      aria-label={label}
      className={cx(styles.iconButton, className)}
      {...rest}
    >
      {children}
    </Button>
  )
})

IconButton.propTypes = {
  label: PropTypes.string.isRequired,
  variant: PropTypes.oneOf(BUTTON_VARIANTS),
  size: PropTypes.oneOf(BUTTON_SIZES),
  className: PropTypes.string,
  children: PropTypes.node,
}

export default Button
